import keycoder from 'keycoder'
import { bool, string, func, array, object, node } from 'prop-types'
import { WithMenu } from '../../hocs'
import Token from '../../Token'
import { renderIcon, kuiperClassNames, stopEvent } from '../../utils'
import Input from '../Input'

import './styles.scss'

class UnwrappedMultiSelect extends Input {
  static propTypes = {
    ...Input.propTypes,
    avatared: bool,
    canSelectAll: bool,
    className: string,
    clearSearchOnSelect: bool,
    icon: string,
    id: string,
    inputIcon: string,
    noOptionsText: string,
    noOptionsElement: object,
    noSearch: bool,
    noSuggestions: bool,
    onChange: func,
    onRemove: func,
    onSelect: func,
    onSuggest: func,
    optionDecorator: func,
    options: array,
    optionsType: string,
    placeholder: string,
    resizeHandle: node,
    selectMatchOnEnter: bool,
    showSelected: bool,
    tokenOptions: object,
    value: array,
    withMenu: bool,
  }

  static defaultProps = {
    ...Input.defaultProps,
    canSelectAll: true,
    inputIcon: 'plus',
    options: [],
    placeholder: 'Type to search...',
    tokenOptions: {},
    value: [],
    withMenu: true,
  }

  constructor(props) {
    super(props)

    this.state = {
      browsing: false,
      inputFocused: false,
    }

    this.trackInputElement = this.trackInputElement.bind(this)
    this.suggestKeyDown = this.suggestKeyDown.bind(this)
    this.suggestKeyUp = this.suggestKeyUp.bind(this)
    this.clearFocusedToken = this.clearFocusedToken.bind(this)
    this.onInputFocus = this.onInputFocus.bind(this)
    this.onInputBlur = this.onInputBlur.bind(this)
    this.inputSuggest = this.inputSuggest.bind(this)
    this.suggest = this.suggest.bind(this)
    this.onFocus = this.onFocus.bind(this)
  }

  classNames(context) {
    return kuiperClassNames(super.classNames(context), 'multiselect', {
      focused: context.props && context.props.focused,
    })
  }

  actionsFor(option) {
    const { actions } = this.props
    if (actions) {
      return Object.keys(actions).reduce(
        (accum, key) => ({
          ...accum,
          [key]: actions[key].bind(this, option),
        }),
        {}
      )
    }
    return actions
  }

  remove(option, keypress = false, e) {
    stopEvent(e)
    if (!keypress) {
      this.clearFocusedToken()
    }
    if (this.props.onRemove) {
      this.props.onRemove(option)
    }
  }

  tokenClick(option, e) {
    stopEvent(e)
    if (!this.props.disabled && !option.isRequired) {
      this.setState({
        focusedTokenIndex: this.props.value.indexOf(option),
      })
    }
  }

  tokenOptions(option) {
    const { tokenOptions, className, disabled, theme } = this.props
    const { tokenClick, remove, actionsFor, focusedToken } = this
    const isRequired = option ? option.isRequired : undefined
    const optionHasFullName = option && option.first_name && option.last_name
    const name = optionHasFullName
      ? `${option.first_name} ${option.last_name}`
      : undefined
    const title = option && option.title ? option.title : undefined

    return {
      ...tokenOptions,
      imageUrl: option ? option.imageUrl : undefined,
      onClick: tokenClick.bind(this, option),
      onRemove: remove.bind(this, option, false),
      actions: actionsFor.bind(this)(option),
      focused: option === focusedToken,
      className: `${className} theme-${theme}`,
      removable: !disabled && !isRequired,
      disabled: isRequired,
      title: name || title,
      color: 'n6',
    }
  }

  get tokenElements() {
    return this.props.value
      .filter(Boolean)
      .map((option, idx) => (
        <Token
          {...option}
          {...this.tokenOptions(option)}
          key={`${option ? option.id : 'option_undefined_'}${idx}`}
        />
      ))
  }

  leftArrowPressed() {
    if (this.tokenFocused) {
      this.setFocusedToken(this.currentFocusIndex - 1)
      return
    }
    if (this.props.term === '') {
      this.editLastToken()
    }
  }

  rightArrowPressed() {
    if (this.tokenFocused) {
      this.setFocusedToken(this.currentFocusIndex + 1)
    }
  }

  clearFocusedToken() {
    this.setState({
      focusedTokenIndex: undefined,
    })
  }

  escapePressed() {
    this.clearFocusedToken()
    this.suggest('')
  }

  suggestKeyUp(e) {
    const char = keycoder.eventToCharacter(e)

    switch (e.keyCode) {
      case 37:
        this.leftArrowPressed(e)
        break
      case 39:
        this.rightArrowPressed(e)
        break
      case 27:
        this.escapePressed()
        break
      case 13:
      case 188:
        this.props.onSuggest('')
        this.setState(() => ({
          browsing: false,
        }))
        break
      default:
        if (char && this.inputElementReference !== e.target) {
          this.suggest(this.props.term + char)
        }
        break
    }
    if (this.props.onKeyUp) {
      this.props.onKeyUp(e)
    }
  }

  editLastToken() {
    this.setFocusedToken(this.props.value.length - 1)
  }

  setFocusedToken(index) {
    const { value } = this.props
    if (value.length > index && index > -1) {
      if (!value[index].isRequired) {
        this.setState({
          focusedTokenIndex: index,
        })
        return
      }
      const lastIndex = index - 1
      this.setFocusedToken(lastIndex)
    }
    if (value.length === index) {
      this.clearFocusedToken()
    }
  }

  get tokenFocused() {
    return this.state.focusedTokenIndex !== undefined
  }

  get currentFocusIndex() {
    return this.state.focusedTokenIndex
  }

  get focusedToken() {
    if (this.tokenFocused) {
      return this.props.value[this.state.focusedTokenIndex]
    }
    return undefined
  }

  deletePressed() {
    const { focusedToken } = this
    if (this.props.term === '') {
      this.editLastToken()
    }
    if (focusedToken) {
      if (focusedToken.removable !== false) {
        const { value } = this.props
        if (this.tokenFocused) {
          this.setFocusedToken(this.currentFocusIndex - 1)
        }
        if (value[this.currentFocusIndex]) {
          this.remove(value[this.currentFocusIndex], true)
        }
      }
    }
  }

  suggestKeyDown(e) {
    switch (e.keyCode) {
      case 8:
      case 46:
        this.deletePressed(e)
        break
      case 40:
        if (!this.state.browsing && this.props.withMenu) {
          this.setState(() => ({
            browsing: true,
          }))
        }
        break
      case 188:
      case 13:
        this.tagEnter()
        break
      default:
        if (this.props.interactionHandlers) {
          this.props.interactionHandlers.onKeyDown(e)
        }
        break
    }
    if (this.props.onKeyDown) {
      this.props.onKeyDown(e)
    }
  }

  tagEnter() {
    const { term, onSelect } = this.props
    const { browsing } = this.state
    if (term !== '' && !browsing) {
      onSelect(term)
    }
  }

  inputSuggest(e) {
    this.suggest(e.target.value)
  }

  suggest(value) {
    if (this.props.onSuggest) {
      this.props.onSuggest(value)
    }
  }

  trackInputElement(element) {
    if (element) {
      this.inputElementReference = element
    }
  }

  onFocus(e) {
    const { interactionHandlers } = this.props
    if (interactionHandlers.onFocus !== undefined) {
      interactionHandlers.onFocus(e)
    }
  }

  onInputFocus(e) {
    if (e) {
      e.stopPropagation()
    }

    this.setState(() => ({
      inputFocused: true,
      browsing: false,
    }))
    this.clearFocusedToken()
  }

  onMultiSelectFocus = () => {
    if (this.props.noSuggestions) {
      return
    }
    if (this.inputElementReference) {
      this.inputElementReference.focus()
    }
  }

  onInputBlur(e) {
    if (e) {
      e.stopPropagation()
    }
    if (this.props.onInputBlur) {
      this.props.onInputBlur(e)
    }
    this.setState(() => ({
      inputFocused: false,
      browsing: false,
    }))
  }

  get inputElement() {
    const { disabled, className, term } = this.props
    return (
      <div className={kuiperClassNames('input')}>
        {renderIcon('plus', {
          width: 8,
          height: 8,
          ariaLabel: this.props.placeholder,
        })}
        <input
          onFocus={this.onInputFocus}
          onBlur={this.onInputBlur}
          placeholder={this.props.placeholder}
          disabled={disabled}
          ref={this.trackInputElement}
          value={term}
          onChange={this.inputSuggest}
          className={kuiperClassNames(className)}
        />
      </div>
    )
  }

  get searchBox() {
    const { disabled, noSearch } = this.props
    if (noSearch || disabled) {
      return null
    }
    return (
      <div
        aria-label="Multiselect Expand/Collapse"
        className={kuiperClassNames('token input add', this.props.className)}
      >
        {this.inputElement}
      </div>
    )
  }

  get input() {
    const { id, interactionHandlers, resizeHandle, disabled, focused, tabbed } =
      this.props
    const { suggestKeyUp, suggestKeyDown } = this
    const classes = kuiperClassNames(
      'input-wrapper',
      'input-wrapper-tokens',
      `theme-${this.props.theme}`,
      this.props.className,
      {
        disabled,
        focused,
        tabbed,
      }
    )
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        id={id}
        className={classes}
        {...interactionHandlers}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onKeyUp={suggestKeyUp}
        onKeyDown={suggestKeyDown}
        onClickCapture={this.onMultiSelectFocus}
      >
        {this.tokenElements}
        {this.searchBox}
        {resizeHandle}
      </div>
    )
  }

  get inputIcon() {
    if (this.props.inputIcon) {
      return renderIcon(this.props.inputIcon, {
        width: 8,
        height: 8,
        ariaMode: 'hidden',
      })
    }

    return null
  }

  get icon() {
    if (this.props.icon) {
      // I'm not sure whether this should be hidden, because I'm not sure what
      // it's used for.
      // - jordan@
      return renderIcon(this.props.icon, { ariaMode: 'hidden' })
    }

    return null
  }

  onBlur = (e) => {
    if (e) {
      e.stopPropagation()
    }

    this.clearFocusedToken()
    if (this.props.onInputBlur) {
      this.props.onInputBlur(e)
    }
  }
}
UnwrappedMultiSelect.displayName =
  'Kuiper: UnwrappedMultiSelect ← MultiSelect ← Inputs'

export default WithMenu(UnwrappedMultiSelect, {
  isMultiSelect: true,
  popoverOptions: {
    options: {
      themeClass: 'popoverjs--dropdown',
      maintainAttachmentWidth: true,
      bodyAttached: false,
      constraintElement: 'scroll',
    },
    triggerEvent: 'manual',
  },
})

export { UnwrappedMultiSelect }
